import { useState } from 'react';

interface Message {
  type: 'success' | 'error';
  text: string;
}

// Ported from docker/admin/src/hooks/useN8nWebhook.ts. Triggers an n8n
// workflow directly from the browser (no admin-api proxy — this is a
// same-shape fire-and-forget call, not data the backend needs to own).
export function useN8nWebhook() {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  // Optional `payload` switches to a JSON POST (for workflows that read a body,
  // e.g. tips-email generation); without it this is the original fire-and-forget
  // GET. Returns whether the trigger succeeded so callers can toast their own way.
  const triggerWebhook = async (url: string, buttonName: string = 'Process', payload?: unknown): Promise<boolean> => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response =
        payload === undefined
          ? await fetch(url, { method: 'GET' })
          : await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!response.ok) {
        throw new Error(`Failed to trigger ${buttonName.toLowerCase()}`);
      }
      setMessage({ type: 'success', text: `${buttonName} started successfully!` });
      return true;
    } catch (error) {
      console.error(`Error triggering ${buttonName}:`, error);
      setMessage({ type: 'error', text: `Failed to start ${buttonName.toLowerCase()}. Please try again.` });
      return false;
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  return { triggerWebhook, isLoading, message };
}
